import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../context/NotificationContext';
import {
  DeveloperRequestsService,
  DeveloperAccessRequest,
  DeveloperRequestStatus,
} from '../services/developer-requests.service';
import styles from './AdminDeveloperRequestsPage.module.css';

export default function AdminDeveloperRequestsPage() {
  const { t } = useTranslation();
  const { addNotification } = useNotification();

  const [requests, setRequests] = useState<DeveloperAccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<DeveloperRequestStatus>('PENDING');
  const [selected, setSelected] = useState<DeveloperAccessRequest | null>(null);
  const [reviewAction, setReviewAction] = useState<'APPROVED' | 'REJECTED' | null>(null);
  const [adminComments, setAdminComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadRequests();
  }, [filter]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const res = await DeveloperRequestsService.getAll({ status: filter });
      setRequests(res.data || []);
    } catch {
      addNotification(t('developer_request.admin_load_error') || 'Error cargando solicitudes.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (req: DeveloperAccessRequest) => {
    setSelected(req);
    setReviewAction(null);
    setAdminComments('');
  };

  const handleDecision = async () => {
    if (!selected || !reviewAction) return;

    setSubmitting(true);
    try {
      await DeveloperRequestsService.review(selected.id, {
        status: reviewAction,
        adminComments: adminComments || undefined,
      });

      addNotification(
        reviewAction === 'APPROVED'
          ? t('developer_request.admin_approve_success')
          : t('developer_request.admin_reject_success'),
        'success',
      );

      setSelected(null);
      setReviewAction(null);
      setAdminComments('');
      loadRequests();
    } catch (error: any) {
      addNotification(error?.message || t('developer_request.admin_load_error'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (status: DeveloperRequestStatus) => {
    const badges: Record<string, { text: string; className: string }> = {
      PENDING: { text: t('developer_request.status_pending'), className: styles.statusPending },
      APPROVED: { text: t('developer_request.status_approved'), className: styles.statusApproved },
      REJECTED: { text: t('developer_request.status_rejected'), className: styles.statusRejected },
    };
    return badges[status] || { text: status, className: '' };
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('developer_request.admin_title')}</h1>
        <p className={styles.subtitle}>{t('developer_request.admin_subtitle')}</p>
      </div>

      <div className={styles.filters}>
        <button
          className={filter === 'PENDING' ? styles.filterActive : styles.filterButton}
          onClick={() => setFilter('PENDING')}
        >
          {t('developer_request.filter_pending')} ({requests.length})
        </button>
        <button
          className={filter === 'APPROVED' ? styles.filterActive : styles.filterButton}
          onClick={() => setFilter('APPROVED')}
        >
          {t('developer_request.filter_approved')}
        </button>
        <button
          className={filter === 'REJECTED' ? styles.filterActive : styles.filterButton}
          onClick={() => setFilter('REJECTED')}
        >
          {t('developer_request.filter_rejected')}
        </button>
      </div>

      {requests.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>✅</div>
          <h2>{t('developer_request.empty_title')}</h2>
          <p>{t('developer_request.empty_subtitle')}</p>
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>{t('developer_request.table_date')}</th>
                <th>{t('common.user')}</th>
                <th>{t('common.status')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => {
                const badge = statusBadge(req.status);
                return (
                  <tr key={req.id}>
                    <td className={styles.idColumn}>#{req.id}</td>
                    <td>{new Date(req.requestedAt).toLocaleDateString('es-MX')}</td>
                    <td>
                      {req.user?.name || 'N/A'}
                      <br />
                      <small className={styles.email}>{req.user?.email}</small>
                    </td>
                    <td>
                      <span className={`${styles.badge} ${badge.className}`}>{badge.text}</span>
                    </td>
                    <td>
                      <button className={styles.btnReview} onClick={() => openModal(req)}>
                        {req.status === 'PENDING' ? t('common.review') : t('common.view_details')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className={styles.modalOverlay} onClick={() => !submitting && setSelected(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button
              className={styles.closeBtn}
              onClick={() => !submitting && setSelected(null)}
              disabled={submitting}
            >
              ✕
            </button>

            <h2 className={styles.modalTitle}>{t('developer_request.review_modal_title')}</h2>

            <div className={styles.modalContent}>
              <div className={styles.proofInfo}>
                <p>
                  <strong>{t('common.user')}:</strong> {selected.user?.name || 'N/A'}
                </p>
                <p>
                  <strong>{t('profile.email_label')}:</strong> {selected.user?.email || 'N/A'}
                </p>
                {selected.user?.phone ? (
                  <p>
                    <strong>{t('profile.phone_label')}:</strong> {selected.user.phone}
                  </p>
                ) : null}
                <p>
                  <strong>{t('developer_request.table_date')}:</strong>{' '}
                  {new Date(selected.requestedAt).toLocaleString('es-MX')}
                </p>
                {selected.decidedAt ? (
                  <p>
                    <strong>{t('common.review_date')}:</strong>{' '}
                    {new Date(selected.decidedAt).toLocaleString('es-MX')}
                  </p>
                ) : null}
              </div>

              {selected.status === 'PENDING' ? (
                <div>
                  <h3>{t('developer_request.review_action')}</h3>
                  <div className={styles.actionButtons}>
                    <button
                      type="button"
                      className={`${styles.btnAction} ${styles.btnApprove} ${
                        reviewAction === 'APPROVED' ? styles.btnActive : ''
                      }`}
                      onClick={() => setReviewAction('APPROVED')}
                      disabled={submitting}
                    >
                      {t('developer_request.action_approve')}
                    </button>
                    <button
                      type="button"
                      className={`${styles.btnAction} ${styles.btnReject} ${
                        reviewAction === 'REJECTED' ? styles.btnActive : ''
                      }`}
                      onClick={() => setReviewAction('REJECTED')}
                      disabled={submitting}
                    >
                      {t('developer_request.action_reject')}
                    </button>
                  </div>

                  <div className={styles.formGroup}>
                    <label>{t('developer_request.internal_notes')}</label>
                    <textarea
                      className={styles.textarea}
                      value={adminComments}
                      onChange={(e) => setAdminComments(e.target.value)}
                      placeholder={t('developer_request.internal_notes_placeholder') || ''}
                      rows={4}
                      disabled={submitting}
                    />
                  </div>

                  <div className={styles.modalActions}>
                    <button className={styles.btnCancel} onClick={() => setSelected(null)} disabled={submitting}>
                      {t('common.cancel')}
                    </button>
                    <button className={styles.btnSubmit} onClick={handleDecision} disabled={!reviewAction || submitting}>
                      {submitting ? t('common.processing') : t('common.confirm')}
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p>
                    <strong>{t('common.status')}:</strong>{' '}
                    {statusBadge(selected.status).text}
                  </p>
                  {selected.adminComments ? (
                    <p>
                      <strong>{t('developer_request.internal_notes')}:</strong> {selected.adminComments}
                    </p>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
